import { PlayerData, Position, Velocity, ScoreEvent } from '../types/GameTypes';

export class Player {
  public id: string;
  public name: string;
  public color: string;
  public position: Position;
  public velocity: Velocity;
  public radius: number;
  public alive: boolean;
  public health: number;
  public maxHealth: number;
  public shielded: boolean;
  public shieldTime: number;
  public stunned: boolean;
  public stunnedTime: number;
  public score: number;
  public eliminations: number;
  public lastGroundTime: number;
  public element: HTMLElement | null = null;
  public arrowElement: HTMLElement | null = null;
  public healthBarElement: HTMLElement | null = null;
  
  private jumpCount: number = 0;
  private maxJumps: number = 2;
  private isGrounded: boolean = false;
  private lastJumpTime: number = 0;
  private jumpCooldown: number = 200; // milliseconds
  private gravityFlipped: boolean = false;
  private gravityFlipTime: number = 0;

  constructor(id: string, name: string, color: string, startPosition: Position, radius: number = 35) {
    this.id = id;
    this.name = name;
    this.color = color;
    this.position = { ...startPosition };
    this.velocity = { vx: 0, vy: 0 };
    this.radius = radius;
    this.alive = true;
    this.health = 100;
    this.maxHealth = 100;
    this.shielded = false;
    this.shieldTime = 0;
    this.stunned = false;
    this.stunnedTime = 0;
    this.score = 0;
    this.eliminations = 0;
    this.lastGroundTime = Date.now();
  }

  jump(direction?: { x: number; y: number }, powerMultiplier: number = 1): boolean {
    if (!this.alive || this.stunned) return false;
    
    const now = Date.now();
    if (now - this.lastJumpTime < this.jumpCooldown) return false;
    
    // Allow double jump if not grounded
    if (!this.isGrounded && this.jumpCount >= this.maxJumps) return false;
    
    this.lastJumpTime = now;
    this.jumpCount++;
    
    const baseJumpForce = 18 * powerMultiplier;
    const gravityMultiplier = this.gravityFlipped ? -1 : 1;
    
    if (direction) {
      // Directional jump based on input
      const magnitude = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
      if (magnitude > 0) {
        this.velocity.vx += (direction.x / magnitude) * baseJumpForce * 0.7;
        this.velocity.vy += (direction.y / magnitude) * baseJumpForce * gravityMultiplier;
      }
    } else {
      // Default upward jump with slight randomness
      this.velocity.vy = -baseJumpForce * gravityMultiplier;
      this.velocity.vx += (Math.random() - 0.5) * 4;
    }
    
    this.isGrounded = false;
    
    if (this.element) {
      this.element.classList.add('jumping');
      setTimeout(() => {
        if (this.element) {
          this.element.classList.remove('jumping');
        }
      }, 300);
    }
    
    return true;
  }

  updatePosition(areaWidth: number, areaHeight: number, gravity: number, airFriction: number, groundFriction: number, bounce: number): void {
    if (!this.alive) return;

    // Update status effects
    this.updateStatusEffects();
    
    // Apply gravity (with potential flip)
    const gravityMultiplier = this.gravityFlipped ? -1 : 1;
    this.velocity.vy += gravity * gravityMultiplier;
    
    // Terminal velocity
    const terminalVelocity = 25;
    this.velocity.vy = Math.max(-terminalVelocity, Math.min(terminalVelocity, this.velocity.vy));
    
    // Apply friction
    const frictionToApply = this.isGrounded ? groundFriction : airFriction;
    this.velocity.vx *= frictionToApply;
    
    // Update position
    this.position.x += this.velocity.vx;
    this.position.y += this.velocity.vy;
    
    // Handle collisions with arena bounds
    this.handleBoundaryCollisions(areaWidth, areaHeight, bounce);
    
    // Check if grounded (for jump reset)
    const wasGrounded = this.isGrounded;
    this.isGrounded = this.position.y + this.radius >= areaHeight - 5;
    
    if (this.isGrounded && !wasGrounded) {
      this.jumpCount = 0; // Reset jump count when landing
      this.lastGroundTime = Date.now();
    }
    
    // Fall elimination (with grace period)
    if (this.position.y > areaHeight + 200) {
      this.takeDamage(this.health, 'fall');
    }
  }

  private handleBoundaryCollisions(areaWidth: number, areaHeight: number, bounce: number): void {
    // Left/Right walls
    if (this.position.x - this.radius <= 0) {
      this.position.x = this.radius;
      this.velocity.vx = -this.velocity.vx * bounce;
    }
    if (this.position.x + this.radius >= areaWidth) {
      this.position.x = areaWidth - this.radius;
      this.velocity.vx = -this.velocity.vx * bounce;
    }
    
    // Ground/Ceiling
    if (this.position.y + this.radius >= areaHeight) {
      this.position.y = areaHeight - this.radius;
      this.velocity.vy = Math.abs(this.velocity.vy) * -bounce;
      this.isGrounded = true;
    }
    if (this.position.y - this.radius <= 0) {
      this.position.y = this.radius;
      this.velocity.vy = Math.abs(this.velocity.vy) * bounce;
    }
  }

  private updateStatusEffects(): void {
    // Shield
    if (this.shielded) {
      this.shieldTime--;
      if (this.shieldTime <= 0) {
        this.shielded = false;
        if (this.element) {
          this.element.classList.remove('shielded');
        }
      }
    }
    
    // Stun
    if (this.stunned) {
      this.stunnedTime--;
      if (this.stunnedTime <= 0) {
        this.stunned = false;
        if (this.element) {
          this.element.classList.remove('stunned');
        }
      }
    }
    
    // Gravity flip
    if (this.gravityFlipped) {
      this.gravityFlipTime--;
      if (this.gravityFlipTime <= 0) {
        this.gravityFlipped = false;
        if (this.element) {
          this.element.classList.remove('gravity-flipped');
        }
      }
    }
  }

  updateDOMElement(): void {
    if (!this.element) return;
    
    this.element.style.left = (this.position.x - this.radius) + 'px';
    this.element.style.top = (this.position.y - this.radius) + 'px';
    
    // Update health bar if health system is enabled
    this.updateHealthBar();
    
    // Add visual effects based on status
    if (this.stunned) {
      this.element.style.filter = 'brightness(0.5) contrast(1.5)';
    } else {
      this.element.style.filter = '';
    }
  }

  private updateHealthBar(): void {
    if (!this.healthBarElement || this.health >= this.maxHealth) return;
    
    const healthPercent = (this.health / this.maxHealth) * 100;
    this.healthBarElement.style.width = healthPercent + '%';
    
    // Color based on health
    if (healthPercent > 60) {
      this.healthBarElement.style.background = '#4CAF50';
    } else if (healthPercent > 30) {
      this.healthBarElement.style.background = '#FF9800';
    } else {
      this.healthBarElement.style.background = '#F44336';
    }
  }

  updateVelocityArrow(): void {
    if (!this.arrowElement) return;
    
    const speed = Math.sqrt(this.velocity.vx * this.velocity.vx + this.velocity.vy * this.velocity.vy);
    
    if (speed > 3) {
      const angle = Math.atan2(this.velocity.vy, this.velocity.vx);
      const degrees = angle * (180 / Math.PI);
      
      this.arrowElement.style.display = 'block';
      this.arrowElement.style.left = (this.position.x + this.radius + 10) + 'px';
      this.arrowElement.style.top = (this.position.y - 12) + 'px';
      this.arrowElement.style.transform = `rotate(${degrees}deg)`;
      this.arrowElement.style.opacity = Math.min(speed / 15, 1).toString();
      
      // Scale arrow based on speed
      const scale = Math.min(speed / 10, 2);
      this.arrowElement.style.fontSize = (24 * scale) + 'px';
    } else {
      this.arrowElement.style.display = 'none';
    }
  }

  collideWith(other: Player, force: number = 8): boolean {
    if (!this.alive || !other.alive) return false;

    const distance = Math.sqrt(
      Math.pow(this.position.x - other.position.x, 2) + 
      Math.pow(this.position.y - other.position.y, 2)
    );

    if (distance < this.radius + other.radius) {
      const angle = Math.atan2(other.position.y - this.position.y, other.position.x - this.position.x);
      
      // Calculate collision force based on relative velocity
      const relativeVelocity = Math.sqrt(
        Math.pow(this.velocity.vx - other.velocity.vx, 2) + 
        Math.pow(this.velocity.vy - other.velocity.vy, 2)
      );
      
      const collisionForce = Math.min(force * (1 + relativeVelocity / 10), force * 2);
      
      // Apply knockback (reduced if shielded)
      const thisMultiplier = this.shielded ? 0.3 : 1;
      const otherMultiplier = other.shielded ? 0.3 : 1;
      
      this.velocity.vx -= Math.cos(angle) * collisionForce * thisMultiplier;
      this.velocity.vy -= Math.sin(angle) * collisionForce * thisMultiplier;
      other.velocity.vx += Math.cos(angle) * collisionForce * otherMultiplier;
      other.velocity.vy += Math.sin(angle) * collisionForce * otherMultiplier;
      
      // Apply damage if collision is strong enough
      if (relativeVelocity > 15) {
        const damage = Math.floor((relativeVelocity - 15) * 2);
        if (!this.shielded) this.takeDamage(damage, 'collision');
        if (!other.shielded) other.takeDamage(damage, 'collision');
      }
      
      return true;
    }
    
    return false;
  }

  takeDamage(amount: number, _source: 'collision' | 'hazard' | 'fall' | 'powerup'): void {
    if (!this.alive || this.shielded) return;
    
    this.health -= amount;
    
    if (this.health <= 0) {
      this.health = 0;
      this.eliminate();
    } else {
      // Damage feedback
      if (this.element) {
        this.element.classList.add('damaged');
        setTimeout(() => {
          if (this.element) {
            this.element.classList.remove('damaged');
          }
        }, 200);
      }
    }
  }

  heal(amount: number): void {
    if (!this.alive) return;
    
    this.health = Math.min(this.health + amount, this.maxHealth);
    
    // Heal feedback
    if (this.element) {
      this.element.classList.add('healing');
      setTimeout(() => {
        if (this.element) {
          this.element.classList.remove('healing');
        }
      }, 500);
    }
  }

  applyPowerUp(type: 'boost' | 'shield' | 'health' | 'mega-jump' | 'gravity-flip'): void {
    switch (type) {
      case 'boost':
        const boostMultiplier = 2.0;
        this.velocity.vx *= boostMultiplier;
        this.velocity.vy *= boostMultiplier;
        break;
        
      case 'shield':
        this.shielded = true;
        this.shieldTime = 50; // 5 seconds at 100ms tick
        if (this.element) {
          this.element.classList.add('shielded');
        }
        break;
        
      case 'health':
        this.heal(30);
        break;
        
      case 'mega-jump':
        this.jump(undefined, 2.0); // Double jump power
        break;
        
      case 'gravity-flip':
        this.gravityFlipped = true;
        this.gravityFlipTime = 30; // 3 seconds
        if (this.element) {
          this.element.classList.add('gravity-flipped');
        }
        break;
    }
  }

  stun(duration: number = 20): void {
    this.stunned = true;
    this.stunnedTime = duration;
    if (this.element) {
      this.element.classList.add('stunned');
    }
  }

  addScore(points: number, _type: ScoreEvent['type']): void {
    this.score += points;
    
    // Visual score popup
    if (this.element) {
      const scorePopup = document.createElement('div');
      scorePopup.className = 'score-popup';
      scorePopup.textContent = `+${points}`;
      scorePopup.style.position = 'absolute';
      scorePopup.style.left = this.position.x + 'px';
      scorePopup.style.top = (this.position.y - 30) + 'px';
      scorePopup.style.color = '#FFD700';
      scorePopup.style.fontWeight = 'bold';
      scorePopup.style.fontSize = '16px';
      scorePopup.style.pointerEvents = 'none';
      scorePopup.style.zIndex = '1000';
      
      this.element.parentNode?.appendChild(scorePopup);
      
      setTimeout(() => {
        if (scorePopup.parentNode) {
          scorePopup.parentNode.removeChild(scorePopup);
        }
      }, 1000);
    }
  }

  eliminate(): void {
    this.alive = false;
    if (this.element) {
      this.element.style.opacity = '0.3';
      this.element.style.transform = 'scale(0.7)';
      this.element.classList.add('eliminated');
    }
  }

  distanceFrom(position: Position): number {
    return Math.sqrt(
      Math.pow(this.position.x - position.x, 2) + 
      Math.pow(this.position.y - position.y, 2)
    );
  }

  toData(): PlayerData {
    return {
      id: this.id,
      name: this.name,
      color: this.color,
      position: { ...this.position },
      velocity: { ...this.velocity },
      radius: this.radius,
      alive: this.alive,
      health: this.health,
      maxHealth: this.maxHealth,
      shielded: this.shielded,
      shieldTime: this.shieldTime,
      stunned: this.stunned,
      stunnedTime: this.stunnedTime,
      score: this.score,
      eliminations: this.eliminations,
      lastGroundTime: this.lastGroundTime
    };
  }
}